/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import Select from "react-select";

import {
  BETWEEN_DATE_AND_TIME_INTERVAL,
  BETWEEN_DATE_INTERVAL,
  BETWEEN_TIME_INTERVAL,
  DATE_AND_TIME_INTERVAL,
  DATE_INTERVAL,
  DYNAMIC,
  TIME_INTERVAL,
  CURRENT_DATE,
  DATE,
  DATE_AND_TIME,
  DATE_AND_TIME_RANGE,
  DATE_RANGE,
  DICOM_INPUT,
  TIME,
  TIME_RANGE,
  DEFAULT_TIMEZONE,
  getSelectStyles,
} from "../../../constant";
import DicomInput from "../../Input/DicomInput";
import Validator from "../validator";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { checkValidatePeriod } from "../../Transforms/helpers";
import { ConditionData, ConditionFieldProps } from "../../../types/rule";
import ExternalError from "../../Transforms/Fields/ExternalError";

const Dynamic: FunctionComponent<ConditionFieldProps> = ({
  disabled,
  id,
  parent,
  errors,
  condition,
  isFromTransform = true,
  onValueChangeHandler,
  isDark = false,
}: ConditionFieldProps) => {
  const { t } = useTranslation("rule");
  const [options, setOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const TypesOptions = [
    {
      label: t("current_date"),
      value: CURRENT_DATE,
    },
    {
      label: t("dicom_input"),
      value: DICOM_INPUT,
    },
  ];

  useEffect(() => {
    if (isFromTransform) {
      setOptions([
        { label: t("date_interval"), value: DATE_INTERVAL },
        { label: t("time_interval"), value: TIME_INTERVAL },
        { label: t("date_and_time_interval"), value: DATE_AND_TIME_INTERVAL },
        { label: t("between_date_interval"), value: BETWEEN_DATE_INTERVAL },
        { label: t("between_time_interval"), value: BETWEEN_TIME_INTERVAL },
        {
          label: t("between_date_and_time_interval"),
          value: BETWEEN_DATE_AND_TIME_INTERVAL,
        },
      ]);
    } else {
      setOptions([
        { label: t("date"), value: DATE },
        { label: t("time"), value: TIME },
        { label: t("date_and_time"), value: DATE_AND_TIME },
        { label: t("date_range"), value: DATE_RANGE },
        { label: t("time_range"), value: TIME_RANGE },
        {
          label: t("date_and_time_range"),
          value: DATE_AND_TIME_RANGE,
        },
      ]);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      dispatchNewValue({ ...condition, type: CURRENT_DATE });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromTransform]);

  const dispatchNewValue = (con: ConditionData) => {
    onValueChangeHandler(
      {
        type: DYNAMIC,
        scope: isFromTransform ? "conditions" : undefined,
        id,
        dynamic: con,
      },
      parent
    );
  };
  const updateTag = async (tag: string) => {
    if (tag) dispatchNewValue({ ...condition, tag });
  };

  const updateValue = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: condition?.operator,
      tag: condition?.tag,
      value: event.currentTarget.value,
      type: condition?.type,
    });
  };

  const updateFrom = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: condition?.operator,
      tag: condition?.tag,
      to: condition?.to || "",
      from: event.currentTarget.value || "",
      type: condition?.type,
    });
  };

  const updateTo = (event: React.FormEvent<HTMLInputElement>) => {
    dispatchNewValue({
      operator: condition?.operator,
      tag: condition?.tag,
      from: condition?.from || "",
      to: event.currentTarget.value || "",
      type: condition?.type,
    });
  };

  const selectOperator = (update: any) => {
    dispatchNewValue({ ...condition, operator: "" });
    setTimeout(() => {
      dispatchNewValue({ ...condition, operator: update?.value, tag: "" });
    }, 5);
  };

  const selectType = (update: any) => {
    dispatchNewValue({ ...condition, type: update?.value });
  };

  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === DYNAMIC) !==
      undefined
    );
  };

  const validateValue = useMemo(() => {
    if (condition?.value && condition?.operator) {
      switch (condition?.operator) {
        case DATE_INTERVAL:
        case DATE:
          return !Validator.validateDynamicPeriod(condition?.value);
        case TIME_INTERVAL:
        case TIME:
          return !Validator.validateDynamicDuration(condition?.value);
        case DATE_AND_TIME_INTERVAL:
        case DATE_AND_TIME:
          const isPeriod =
            condition?.value && checkValidatePeriod(condition?.value);
          return isPeriod
            ? !Validator.validateDynamicPeriod(condition?.value)
            : !Validator.validateDynamicDuration(condition?.value);
        default:
          return false;
      }
    }
    return false;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  const invalidFrom = useMemo(() => {
    if (condition?.from && condition?.operator) {
      switch (condition?.operator) {
        case BETWEEN_DATE_INTERVAL:
        case DATE_RANGE:
          return !Validator.validateDynamicPeriod(condition?.from);
        case BETWEEN_TIME_INTERVAL:
        case TIME_RANGE:
          return !Validator.validateDynamicDuration(condition?.from);
        case BETWEEN_DATE_AND_TIME_INTERVAL:
        case DATE_AND_TIME_RANGE:
          const isPeriod =
            condition?.from && checkValidatePeriod(condition?.from);
          return isPeriod
            ? !Validator.validateDynamicPeriod(condition?.from)
            : !Validator.validateDynamicDuration(condition?.from);
        default:
          return false;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  const invalidTo = useMemo(() => {
    if (condition?.to && condition?.operator) {
      switch (condition?.operator) {
        case BETWEEN_DATE_INTERVAL:
        case DATE_RANGE:
          return !Validator.validateDynamicPeriod(condition?.to);
        case BETWEEN_TIME_INTERVAL:
        case TIME_RANGE:
          return !Validator.validateDynamicDuration(condition?.to);
        case BETWEEN_DATE_AND_TIME_INTERVAL:
        case DATE_AND_TIME_RANGE:
          const isPeriod = condition?.to && checkValidatePeriod(condition?.to);
          return isPeriod
            ? !Validator.validateDynamicPeriod(condition?.to)
            : !Validator.validateDynamicDuration(condition?.to);
        default:
          return false;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  const isShowFromAndTo = useMemo(() => {
    if (isFromTransform) {
      return (
        condition?.operator &&
        [
          BETWEEN_DATE_AND_TIME_INTERVAL,
          BETWEEN_DATE_INTERVAL,
          BETWEEN_TIME_INTERVAL,
        ].includes(condition?.operator)
      );
      // eslint-disable-next-line no-else-return
    } else {
      return (
        condition?.operator &&
        [DATE_RANGE, TIME_RANGE, DATE_AND_TIME_RANGE].includes(
          condition?.operator
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, isFromTransform]);

  const isShowValue = useMemo(() => {
    if (isFromTransform) {
      // CONDITIONS
      return (
        condition?.operator &&
        [DATE_AND_TIME_INTERVAL, DATE_INTERVAL, TIME_INTERVAL].includes(
          condition?.operator
        )
      );
      // eslint-disable-next-line no-else-return
    } else {
      // TRANSFORM
      return (
        condition?.operator &&
        [DATE, TIME, DATE_AND_TIME].includes(condition?.operator)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, isFromTransform]);

  return (
    <div className="row">
      <div className="col-md-4 select mb-1  col">
        <Select
          styles={getSelectStyles(isDark)}
          menuPlacement="auto"
          menuPosition="fixed"
          isDisabled={disabled}
          id={`${id}_operator`}
          name="operator"
          classNamePrefix="form-control-select"
          options={options}
          placeholder={t("select_operator")}
          onChange={selectOperator}
          value={options.find(
            (option) => option?.value === condition?.operator
          )}
        />
        {hasError() && !condition?.operator && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_operator`}
          >
            {t("field_required")}
          </label>
        )}
      </div>
      {condition?.operator && condition?.type && !isFromTransform && (
        <div className="select mb-1 col-md-4 col">
          <Select
            menuPlacement="auto"
            styles={getSelectStyles(isDark)}
            menuPosition="fixed"
            isDisabled={disabled}
            id={`${id}_type`}
            name="type"
            classNamePrefix="form-control-select"
            options={TypesOptions}
            placeholder={t("select_type")}
            onChange={selectType}
            value={TypesOptions.find(
              (option) => option?.value === condition?.type
            )}
          />
          {hasError() && !condition?.type && (
            <label className="validation-invalid-label" htmlFor={`${id}_type`}>
              {t("field_required")}
            </label>
          )}
        </div>
      )}
      {condition?.operator && condition?.type === CURRENT_DATE && (
        <div className="col-md-4 col-sm-12 mb-1">
          <TimezoneSelect
            name="timeZone"
            onChange={(val) => {
              dispatchNewValue({
                ...condition,
                timeZone: val.value,
              });
            }}
            styles={getSelectStyles(isDark)}
            timezones={allTimezones}
            value={condition?.timeZone || DEFAULT_TIMEZONE.value}
            isSearchable
            isDisabled={disabled}
          />
        </div>
      )}

      {condition?.operator && (
        <div className="col-md-4 col-sm-12 mb-1">
          <DicomInput
            isDark={isDark}
            onChange={updateTag}
            operator={condition?.operator}
            isSearchByVr
            initialValue={condition?.tag}
            disabled={disabled}
          />
          {hasError() && !condition?.tag && (
            <label className="validation-invalid-label" htmlFor={`${id}_tag`}>
              {t("field_required")}
            </label>
          )}
          {hasError() &&
            condition?.tag &&
            !Validator.validateTag(condition?.tag) && (
              <label className="validation-invalid-label" htmlFor={`${id}_tag`}>
                {t("tag_format_invalid")}
              </label>
            )}
          <ExternalError
            errors={errors}
            id={id}
            field="tag"
            htmlFor={`${id}_tag`}
          />
        </div>
      )}

      {isShowValue && (
        <div className="col-md-4 col-sm-12">
          <input
            disabled={disabled}
            id={`${id}_value`}
            type="text"
            name="value"
            className="form-control mb-1"
            placeholder={t("value")}
            onChange={updateValue}
            value={condition?.value?.toUpperCase()}
          />
          {hasError() && !condition?.value && (
            <label className="validation-invalid-label" htmlFor={`${id}_value`}>
              {t("field_required")}
            </label>
          )}
          {hasError() && condition?.value && validateValue && (
            <label className="validation-invalid-label" htmlFor={`${id}_value`}>
              {t("value_format_invalid")}
            </label>
          )}
        </div>
      )}

      {isShowFromAndTo && (
        <>
          <div
            className={`${isFromTransform ? "col-md-2" : "col-md-4"} col-sm-12`}
          >
            <input
              disabled={disabled}
              type="text"
              name="from"
              id={`${id}_from`}
              className="form-control mb-1"
              value={condition?.from?.toUpperCase()}
              placeholder={t("from")}
              onChange={updateFrom}
            />
            {hasError() && !condition?.from && !condition?.to && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_from`}
              >
                {t("field_required")}
              </label>
            )}
            {hasError() && condition?.from && invalidFrom && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_value`}
              >
                {t("value_format_invalid")}
              </label>
            )}
          </div>
          <div
            className={`${isFromTransform ? "col-md-2" : "col-md-4"} col-sm-12`}
          >
            <input
              disabled={disabled}
              type="text"
              name="to"
              id={`${id}_to`}
              className="form-control mb-1"
              value={condition?.to?.toUpperCase()}
              placeholder={t("to")}
              onChange={updateTo}
            />
            {hasError() && !condition?.to && !condition?.from && (
              <label className="validation-invalid-label" htmlFor={`${id}_to`}>
                {t("field_required")}
              </label>
            )}
            {hasError() && condition?.to && invalidTo && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_value`}
              >
                {t("value_format_invalid")}
              </label>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dynamic;
